import React, { useEffect, useState, useMemo } from "react";
import MainLayout from "../../Layout/mainLayout";
import "./Email.css";
import { useDispatch, useSelector } from "react-redux";
import {
  sendEmailNotification,
  getUserData,
  scheduleEmail,
} from "../../Redux/Actions/emailActions";
import SelectInput from "../../Global Functions/selectInput";
import {
  viewCityListPagination,
  viewCountryListPagination,
  viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";

const USER_TYPE_OPTIONS = ["user", "consultant", "builder"].map((o) => ({
  label: o,
  value: o,
}));

const toSelectOptions = (arr, labelKey, valueKey = "_id") => {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => ({ label: item[labelKey], value: item[valueKey] }));
};

const CheckIcon = () => (
  <svg
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="2 6 5 9 10 3" />
  </svg>
);

const ConfirmModal = ({ onConfirm, onCancel }) => (
  <div className="ntf-modal-overlay">
    <div className="ntf-modal">
      <div className="ntf-modal-badge ntf-modal-badge--send">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </div>
      <h3>Send Email</h3>
      <p>
        You're about to send this email to the selected recipients. This cannot
        be undone.
      </p>
      <div className="ntf-modal-btns">
        <button onClick={onCancel} className="ntf-btn ntf-btn-ghost">
          Cancel
        </button>
        <button onClick={onConfirm} className="ntf-btn ntf-btn-accent">
          Send Now
        </button>
      </div>
    </div>
  </div>
);

const ScheduleModal = ({ onClose, onSchedule }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = () => {
    if (!date || !time) {
      alert("Please select both date and time");
      return;
    }
    onSchedule({ date, time });
    onClose();
  };

  return (
    <div className="ntf-modal-overlay">
      <div className="ntf-modal">
        <div className="ntf-modal-badge ntf-modal-badge--schedule">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <h3>Schedule Email</h3>
        <p>
          Pick a date and time to automatically send this email to your
          recipients.
        </p>
        <div className="ntf-modal-form">
          <div className="ntf-modal-cols">
            <div className="ntf-modal-field">
              <label>Date</label>
              <input
                type="date"
                value={date}
                min={today}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="ntf-modal-field">
              <label>Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="ntf-modal-btns">
          <button onClick={onClose} className="ntf-btn ntf-btn-ghost">
            Cancel
          </button>
          <button onClick={handleSubmit} className="ntf-btn ntf-btn-accent">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 15, height: 15 }}
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Confirm Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

const combineToUTCISO = (date, time) =>
  new Date(`${date}T${time}:00`).toISOString();

const EmailTemplates = () => {
  const dispatch = useDispatch();

  const [email_for, setEmail_for] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [html, setHtml] = useState("");
  const [type, setType] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendPayload, setSendPayload] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const { userData } = useSelector((s) => s.emailTemplates || {});
  const { countryList } = useSelector((s) => s.country || {});
  const { states } = useSelector((s) => s.states || {});
  const cityState = useSelector((s) => s.city || {});
  const cityData = Array.isArray(cityState?.citiesAllList?.data?.data)
    ? cityState.citiesAllList.data.data
    : [];
  const countryData = countryList?.data;
  const stateData = states?.data;

  const { admin } = useSelector((s) => s.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  useEffect(() => {
    dispatch(viewCountryListPagination());
  }, [dispatch]);

  useEffect(() => {
    if (country) dispatch(viewStateListPagination({ country }));
    if (state) dispatch(viewCityListPagination({ state, country, page: 1, limit: 1000 }));
  }, [dispatch, country, state]);

  useEffect(() => {
    if (!email_for) return;
    if (email_for === "user") {
      dispatch(getUserData({ list_for: email_for, page: 1, limit: 1000, country_id: country || undefined, filter: searchTerm }));
    } else {
      if (!country || !state || !city) return;
      dispatch(getUserData({ list_for: email_for, page: 1, limit: 1000, country_id: country, state_id: state, city_id: city, filter: searchTerm }));
    }
  }, [dispatch, email_for, country, state, city, searchTerm]);

  const users = useMemo(() => {
    return Array.isArray(userData?.data)
      ? userData.data.map((u) => ({ name: u?.name || "", uid: String(u?.id || ""), id: u?._id || "" }))
      : [];
  }, [userData]);

  const handleTypeChange = (value) =>
    setType((prev) => (prev === value ? "" : value));

  const handleUserCheck = (id) =>
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  const needsCity = email_for === "builder" || email_for === "consultant";

  // Recipients are unlocked only when all required location fields are filled
  const recipientsReady = email_for && country && (!needsCity || (state && city));

  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    setState("");
    setCity("");
    setType("");          // reset recipient selection when location changes
    setSelectedUsers([]);
  };

  const handleStateChange = (e) => {
    setState(e.target.value);
    setCity("");
    setType("");
    setSelectedUsers([]);
  };

  const preparePayload = () => ({
    subject,
    message,
    type,
    email_for,
    html,
    list: selectedUsers,
  });

  const prepareAndShowConfirm = (e) => {
    e.preventDefault();
    setSendPayload(preparePayload());
    setShowConfirm(true);
  };

  const openScheduleModal = (e) => {
    e.preventDefault();
    setSendPayload(preparePayload());
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = ({ date, time }) => {
    const payload = preparePayload();
    dispatch(scheduleEmail({ ...payload, schedule_at: combineToUTCISO(date, time) })).then((res) => {
      if (res?.success) { toast.success("Email scheduled successfully"); resetForm(); }
      else toast.error("Error scheduling email");
    });
  };

  const handleConfirmYes = async () => {
    setShowConfirm(false);
    if (!sendPayload) return;
    setIsSending(true);
    const res = await dispatch(sendEmailNotification(sendPayload));
    setIsSending(false);
    if (res?.success) toast.success("Email sent");
    else toast.error("Error sending email");
    resetForm();
  };

  const handleConfirmNo = () => { setShowConfirm(false); setSendPayload(null); };

  const resetForm = () => {
    setEmail_for(""); setState(""); setCountry(""); setCity(""); setSubject(""); setMessage("");
    setType(""); setSelectedUsers([]); setSearchTerm(""); setSendPayload(null); setHtml("");
  };

  const isFormValid = () => {
    if (!email_for || !country) return false;
    if ((email_for === "builder" || email_for === "consultant") && (!state || !city)) return false;
    if (!subject?.trim() || !message?.trim()) return false;
    if (!type) return false;
    if (type === "selected" && selectedUsers.length === 0) return false;
    return true;
  };

  return (
    <div>
      <MainLayout>
        <div className="ntf-page">
          {/* TOP BAR */}
          <div className="ntf-topbar">
            <div>
              <h1 className="ntf-page-title">Email Templates</h1>
              <p className="ntf-page-sub">
                Compose and send targeted emails to your users
              </p>
            </div>
            {canAdd && canEdit && (
              <div className="ntf-actions">
                <button
                  className="ntf-btn ntf-btn-ghost"
                  onClick={openScheduleModal}
                  disabled={!isFormValid()}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Schedule
                </button>
                <button
                  onClick={prepareAndShowConfirm}
                  className="ntf-btn ntf-btn-accent"
                  disabled={!isFormValid() || isSending}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  {isSending ? "Sending…" : "Send Now"}
                </button>
              </div>
            )}
          </div>

          <div className="ntf-layout">
            <div className="ntf-left">
              {/* TARGETING CARD */}
              <div className="ntf-card">
                <div className="ntf-card-head">
                  <div className="ntf-card-head-left">
                    <div className="ntf-card-icon ntf-card-icon--purple">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                      </svg>
                    </div>
                    <div>
                      <div className="ntf-card-title">Targeting</div>
                      <div className="ntf-card-desc">Define audience scope</div>
                    </div>
                  </div>
                </div>
                <div className="ntf-card-body">
                  <div className="ntf-row-2">
                    <div className="ntf-field">
                      <label className="ntf-label">
                        User Type <span className="req">*</span>
                      </label>
                      <div className="ntf-select-wrap">
                        <SelectInput
                          required
                          name="email_for"
                          value={email_for}
                          placeholder="Select an option"
                          options={USER_TYPE_OPTIONS}
                          onChange={(e) => { setEmail_for(e.target.value); setState(""); setCity(""); setType(""); setSelectedUsers([]); }}
                          className="ntf-select"
                        />
                      </div>
                    </div>
                    <div className="ntf-field">
                      <label className="ntf-label">
                        Country <span className="req">*</span>
                      </label>
                      <div className="ntf-select-wrap">
                        <SelectInput
                          required
                          name="country"
                          value={country}
                          placeholder="Select an option"
                          options={toSelectOptions(countryData, "country_name")}
                          onChange={handleCountryChange}
                          className="ntf-select"
                        />
                      </div>
                    </div>
                  </div>

                  {(email_for === "builder" || email_for === "consultant") && (
                    <div className="ntf-row-2">
                      <div className="ntf-field">
                        <label className="ntf-label">State</label>
                        <div className="ntf-select-wrap">
                          <SelectInput
                            disabled={!country}
                            required
                            name="State"
                            value={state}
                            placeholder="Select"
                            options={toSelectOptions(stateData, "state_name")}
                            onChange={handleStateChange}
                            className="ntf-select"
                          />
                        </div>
                      </div>
                      <div className="ntf-field">
                        <label className="ntf-label">City</label>
                        <div className="ntf-select-wrap">
                          <SelectInput
                            disabled={!state}
                            required
                            name="City"
                            value={city}
                            placeholder="Select"
                            options={toSelectOptions(cityData, "city_name")}
                            onChange={(e) => setCity(e.target.value)}
                            className="ntf-select"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* CONTENT CARD */}
              <div className="ntf-card">
                <div className="ntf-card-head">
                  <div className="ntf-card-head-left">
                    <div className="ntf-card-icon ntf-card-icon--amber">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </div>
                    <div>
                      <div className="ntf-card-title">Content</div>
                      <div className="ntf-card-desc">Write your email</div>
                    </div>
                  </div>
                </div>
                <div className="ntf-card-body">
                  <div className="ntf-field">
                    <label className="ntf-label">
                      Subject <span className="req">*</span>
                      <span className="hint">Max 60</span>
                    </label>
                    <input
                      className="ntf-input"
                      type="text"
                      placeholder="Enter email subject…"
                      maxLength={60}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value.trimStart())}
                    />
                    <span className="ntf-char-count">{subject.length} / 60</span>
                  </div>

                  <div className="ntf-field">
                    <label className="ntf-label">
                      Message <span className="req">*</span>
                      <span className="hint">Max 500</span>
                    </label>
                    <textarea
                      className="ntf-textarea"
                      placeholder="Write the email body…"
                      maxLength={500}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    <span className="ntf-char-count">{message.length} / 500</span>
                  </div>

                  <div className="ntf-field">
                    <label className="ntf-label">
                      HTML Content
                      <span className="hint">Optional</span>
                    </label>
                    <textarea
                      className="ntf-textarea ntf-textarea--html"
                      placeholder="Paste custom HTML here…"
                      value={html}
                      onChange={(e) => setHtml(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RECIPIENTS CARD */}
            <div className="ntf-recipient-card">
              <div className="ntf-card-head">
                <div className="ntf-card-head-left">
                  <div className="ntf-card-icon ntf-card-icon--green">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </div>
                  <div>
                    <div className="ntf-card-title">Recipients</div>
                    <div className="ntf-card-desc">Who gets this email</div>
                  </div>
                </div>
              </div>
              <div className="ntf-card-body">
                <div className="ntf-options-list">
                  <div
                    className={`ntf-option-row ${type === "all" ? "ntf-option-row--active" : ""} ${!recipientsReady ? "ntf-option-row--disabled" : ""}`}
                    onClick={() => recipientsReady ? handleTypeChange("all") : null}
                  >
                    <div className="ntf-option-icon ntf-option-icon--all">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                      </svg>
                    </div>
                    <div className="ntf-option-text">
                      <strong>All Users</strong>
                      <span>Broadcast to every user in selected country</span>
                    </div>
                    <div className="ntf-option-checkbox">
                      <div className={`ntf-custom-check ${type === "all" ? "ntf-custom-check--checked" : ""}`}>
                        <CheckIcon />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`ntf-option-row ${type === "selected" ? "ntf-option-row--active" : ""} ${!recipientsReady ? "ntf-option-row--disabled" : ""}`}
                    onClick={() => recipientsReady ? handleTypeChange("selected") : null}
                  >
                    <div className="ntf-option-icon ntf-option-icon--custom">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <div className="ntf-option-text">
                      <strong>Custom Selection</strong>
                      <span>Hand-pick specific users to email</span>
                    </div>
                    <div className="ntf-option-checkbox">
                      <div className={`ntf-custom-check ${type === "selected" ? "ntf-custom-check--checked" : ""}`}>
                        <CheckIcon />
                      </div>
                    </div>
                  </div>
                </div>

                {!recipientsReady && (
                  <div className="ntf-warning-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {!email_for
                      ? "Select a user type to continue"
                      : !country
                      ? "Select a country to continue"
                      : needsCity && !state
                      ? "Select a state to continue"
                      : needsCity && !city
                      ? "Select a city to enable recipient options"
                      : "Complete the targeting fields above"}
                  </div>
                )}

                {type === "selected" && recipientsReady && (
                  <div className="ntf-user-panel">
                    <div className="ntf-user-search-bar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by name or UID…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="ntf-user-list-header">
                      <span>Name</span>
                      <span>UID</span>
                    </div>
                    <ul className="ntf-user-list">
                      {users.length === 0 ? (
                        <li>
                          <div className="ntf-empty-state">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                            </svg>
                            No users found
                          </div>
                        </li>
                      ) : (
                        users.map((user) => (
                          <li key={user.id}>
                            <label className="ntf-user-item">
                              <input
                                type="checkbox"
                                value={user.id}
                                onChange={() => handleUserCheck(user.id)}
                                checked={selectedUsers.includes(user.id)}
                              />
                              <div className="ntf-user-item-info">
                                <span className="user-name">{user.name}</span>
                                <span className="user-uid">{user.uid}</span>
                              </div>
                            </label>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}

                {type === "selected" && selectedUsers.length > 0 && (
                  <div className="ntf-selected-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""} selected
                  </div>
                )}
              </div>
            </div>
          </div>

          {showScheduleModal && (
            <ScheduleModal
              onClose={() => setShowScheduleModal(false)}
              onSchedule={handleScheduleSubmit}
            />
          )}
          {showConfirm && (
            <ConfirmModal onConfirm={handleConfirmYes} onCancel={handleConfirmNo} />
          )}
        </div>
      </MainLayout>
    </div>
  );
};

export default EmailTemplates;